import { useState } from 'react'
import { latestVersions } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { activeChild, attachableBlocks, type PlanBlock } from '../../store/selectors'
import { todayISO } from '../../lib/dates'

/**
 * The rarer actions, folded behind one quiet button so the row that hands
 * her the game never wraps: put it on today's activity, print it, or remove
 * it (every version).
 */
export function GameMenu({ versions, onPrint, onRemoved, compact }: {
  versions: any[]
  onPrint?: () => void
  /** Where to go once it is gone: the game's page leaves; a list row has nothing to do. */
  onRemoved?: () => void
  /** In a list row: a smaller button, the menu opening under the row. */
  compact?: boolean
}) {
  const [open, setOpen] = useState<'none' | 'menu' | 'attach' | 'remove'>('none')
  const child = useAuroraStore(activeChild) as any
  const blocks = useAuroraStore((s: any) => attachableBlocks(s, todayISO(child?.timezone))) as PlanBlock[]
  const current = latestVersions(versions)[0]
  const act = () => auroraStore.getState() as any
  const toggle = (m: typeof open) => setOpen((o) => (o === m ? 'none' : m))
  return (
    <>
      <button className={`btn btn-quiet gp-more${compact ? ' is-compact' : ''}`} aria-label="More" aria-expanded={open !== 'none'} onClick={() => setOpen((o) => (o === 'none' ? 'menu' : 'none'))}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
      </button>
      {open === 'menu' && (
        <div className="gp-menu" role="menu">
          {blocks.length > 0 && <button role="menuitem" className="gp-menu-item" onClick={() => toggle('attach')}>Put it on today</button>}
          {onPrint && <button role="menuitem" className="gp-menu-item" onClick={() => { setOpen('none'); onPrint() }}>Print</button>}
          <button role="menuitem" className="gp-menu-item is-danger" onClick={() => toggle('remove')}>Remove</button>
        </div>
      )}
      {open === 'attach' && (
        <div className="gp-menu">
          <p className="gp-menu-head">Put it on an activity today</p>
          {blocks.map((b) => (
            <button key={b.id} className="gp-menu-item" onClick={() => { act().attachMaterialToBlock(current._id, b.id); setOpen('none') }}>
              {b.title}
            </button>
          ))}
        </div>
      )}
      {open === 'remove' && (
        <div className="gp-menu">
          <p className="gp-menu-head">Remove this game{versions.length > 1 ? ` and its ${versions.length} versions` : ''}?</p>
          <div className="row row-2">
            <button className="btn btn-ghost btn-sm" onClick={() => { for (const v of versions) act().deleteArtifact(v._id); setOpen('none'); onRemoved?.() }}>Remove</button>
            <button className="btn btn-quiet btn-sm" onClick={() => setOpen('none')}>Keep it</button>
          </div>
        </div>
      )}
    </>
  )
}
